import React, { useState, useEffect, useRef } from "react";
import { IoCloseOutline, IoEyeOutline, IoEyeOffOutline, IoMailOutline, IoCheckmarkCircleOutline } from "react-icons/io5";
import "./RegisterModal.scss";
import { useRegisterUser } from "../services/AuthService";
import type {
  RegisterStepOneData,
  RegisterStepTwoData,
} from "../../../../shared/models/RegisterRequest";

interface RegisterModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const COMPANY_SIZES = [
  "1",
  "2 bis 5",
  "6 bis 10",
  "11 bis 50",
  "51 bis 200",
  "201 bis 500",
  "Mehr als 500",
];

const INDUSTRIES = [
  "Computer-Software",
  "IT-Dienstleistungen",
  "Telekommunikation",
  "Finanzdienstleistungen",
  "Handel",
  "Produktion",
  "Gesundheitswesen",
  "Bildung",
  "Sonstige",
];

const MODULES: { code: string; label: string }[] = [
  { code: "crm",        label: "CRM" },
  { code: "calculator", label: "Margenkalkulator" },
  { code: "cockpit",    label: "Cockpit" },
  { code: "sales",    label: "Sales" },
];

// Akzeptiert URLs mit oder ohne Protokoll: "www.example.de", "example.de", "https://example.de"
const WEBSITE_REGEX =
  /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/[\w\-./?%&=]*)?$/i;

export default function RegisterModal({ isOpen, onClose }: RegisterModalProps) {
  const [step, setStep] = useState<1 | 2>(1);
  const [showPassword, setShowPassword] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState<string>("");
  const [isModuleDropdownOpen, setIsModuleDropdownOpen] = useState(false);
  const moduleDropdownRef = useRef<HTMLDivElement>(null);
  const [stepOne, setStepOne] = useState<RegisterStepOneData>({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
  });
  const [stepTwo, setStepTwo] = useState<RegisterStepTwoData>({
    companyName: "",
    companyWebsite: "",
    companySize: "",
    industry: "",
    module: [],
  });

  // React-Query-Mutation für die Registrierung
  const registerMutation = useRegisterUser();

  // Schließt das Module-Dropdown bei Klick außerhalb
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        moduleDropdownRef.current &&
        !moduleDropdownRef.current.contains(event.target as Node)
      ) {
        setIsModuleDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!isOpen) return null;

  const handleStepOneChange = (
    field: keyof RegisterStepOneData,
    value: string,
  ) => {
    setStepOne((prev) => ({ ...prev, [field]: value }));
  };

  const handleStepTwoChange = (
    field: keyof RegisterStepTwoData,
    value: string | string[],
  ) => {
    setStepTwo((prev) => ({ ...prev, [field]: value }));
  };

  const handleStepOneSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStep(2);
  };

  const handleStepTwoSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    // Eigene URL-Validierung (akzeptiert auch "www.example.de" ohne Protokoll)
    if (!WEBSITE_REGEX.test(stepTwo.companyWebsite.trim())) {
      setSubmitError(
        "Bitte gib eine gültige Website ein (z. B. www.example.de).",
      );
      return;
    }

    // Daten aus beiden Schritten zusammenführen und an den AuthService übergeben.
    registerMutation.mutate(
      { ...stepOne, ...stepTwo },
      {
        onSuccess: (response) => {
          console.log("[RegisterModal] backendApi response:", response);
          setRegisteredEmail(stepOne.email);
          setRegistrationSuccess(true);
        },
        onError: (error) => {
          console.error("[RegisterModal] Fehler bei der Registrierung:", error);

          // Versuche, eine spezifische Fehlermeldung aus der Backend-Antwort zu extrahieren
          let message =
            "Die Registrierung ist fehlgeschlagen. Bitte erneut versuchen.";

          const axiosError = error as {
            response?: {
              data?: {
                detail?: string;
                message?: string;
                title?: string;
              };
            };
          };

          if (axiosError?.response?.data?.detail) {
            message = axiosError.response.data.detail;
          } else if (axiosError?.response?.data?.message) {
            message = axiosError.response.data.message;
          } else if (axiosError?.response?.data?.title) {
            message = axiosError.response.data.title;
          } else if (error.message) {
            message = error.message;
          }

          setSubmitError(message);
        },
      },
    );
  };

  //Setzt das Modal zurueck und schliesst es
  const handleClose = () => {
    setStep(1);
    setSubmitError(null);
    setRegistrationSuccess(false);
    setRegisteredEmail("");
    setStepOne({ firstName: "", lastName: "", email: "", password: "" });
    setStepTwo({
      companyName: "",
      companyWebsite: "",
      companySize: "",
      industry: "",
      module: [],
    });
    registerMutation.reset();
    onClose();
  };

  // Passwort-Validierung
  const passwordValidations = {
    minLength: stepOne.password.length >= 8,
    hasSpecialChar: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(stepOne.password),
  };

  const isStepOneValid =
    stepOne.firstName.trim() !== "" &&
    stepOne.lastName.trim() !== "" &&
    stepOne.email.trim() !== "" &&
    passwordValidations.minLength &&
    passwordValidations.hasSpecialChar;

  const isWebsiteValid = WEBSITE_REGEX.test(stepTwo.companyWebsite.trim());

  const isStepTwoValid =
    stepTwo.companyName.trim() !== "" &&
    stepTwo.companyWebsite.trim() !== "" &&
    isWebsiteValid &&
    stepTwo.companySize !== "" &&
    stepTwo.industry !== "" &&
    stepTwo.module.length > 0;

  // Erfolgsansicht nach erfolgreicher Registrierung
  if (registrationSuccess) {
    return (
      <div className="register-overlay">
        <div className="register-modal" onClick={(e) => e.stopPropagation()}>
          <button
            className="register-modal__close"
            onClick={handleClose}
            aria-label="Schliessen"
          >
            <IoCloseOutline />
          </button>

          <div className="register-modal__content register-modal__content--success">
            <div className="register-modal__success-icon">
              <IoCheckmarkCircleOutline />
            </div>

            <h2 className="register-modal__title">Fast geschafft!</h2>

            <p className="register-modal__success-text">
              Wir haben Ihnen eine Verifizierungs-E-Mail an<br />
              <strong>{registeredEmail}</strong> gesendet.
            </p>

            <p className="register-modal__success-hint">
              Bitte klicken Sie auf den Link in der E-Mail, um Ihre
              E-Mail-Adresse zu bestätigen und Ihren Account zu aktivieren.
              Der Link ist 24 Stunden gueltig.
            </p>

            <div className="register-modal__success-email">
              <IoMailOutline />
              <span>Pruefen Sie auch Ihren Spam-Ordner.</span>
            </div>

            <button
              type="button"
              className="register-modal__btn register-modal__btn--primary register-modal__btn--full"
              onClick={handleClose}
            >
              Verstanden
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="register-overlay">
      <div className="register-modal" onClick={(e) => e.stopPropagation()}>
        <button
          className="register-modal__close"
          onClick={handleClose}
          aria-label="Schliessen"
        >
          <IoCloseOutline />
        </button>

        <div className="register-modal__content">
          {step === 1 ? (
            <>
              <h2 className="register-modal__title">Konto erstellen</h2>
              <p className="register-modal__subtitle">
                Starten Sie in wenigen Schritten
              </p>

              <form
                className="register-modal__form"
                onSubmit={handleStepOneSubmit}
                noValidate
              >
                <div className="register-modal__field">
                  <label htmlFor="firstName">
                    Dein Name <span className="register-modal__required">*</span>
                  </label>
                  <div className="register-modal__row">
                    <input
                      type="text"
                      id="firstName"
                      placeholder="Vorname"
                      value={stepOne.firstName}
                      onChange={(e) =>
                        handleStepOneChange("firstName", e.target.value)
                      }
                      required
                    />
                    <input
                      type="text"
                      id="lastName"
                      placeholder="Nachname"
                      value={stepOne.lastName}
                      onChange={(e) =>
                        handleStepOneChange("lastName", e.target.value)
                      }
                      required
                    />
                  </div>
                </div>

                <div className="register-modal__field">
                  <label htmlFor="email">
                    E-Mail <span className="register-modal__required">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    placeholder="E-Mail-Adresse"
                    value={stepOne.email}
                    onChange={(e) =>
                      handleStepOneChange("email", e.target.value)
                    }
                    required
                  />
                </div>

                <div className="register-modal__field">
                  <label htmlFor="password">
                    Passwort <span className="register-modal__required">*</span>
                  </label>
                  <div className="register-modal__password-wrapper">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      placeholder="Passwort erstellen"
                      value={stepOne.password}
                      onChange={(e) =>
                        handleStepOneChange("password", e.target.value)
                      }
                      required
                    />
                    <button
                      type="button"
                      className="register-modal__password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={
                        showPassword ? "Passwort verbergen" : "Passwort anzeigen"
                      }
                    >
                      {showPassword ? <IoEyeOffOutline /> : <IoEyeOutline />}
                    </button>
                  </div>

                  <div className="register-modal__validations">
                    <div
                      className={`register-modal__validation ${
                        passwordValidations.minLength ? "is-valid" : ""
                      }`}
                    >
                      <span className="register-modal__validation-dot" />
                      Mindestens 8 Zeichen
                    </div>
                    <div
                      className={`register-modal__validation ${
                        passwordValidations.hasSpecialChar ? "is-valid" : ""
                      }`}
                    >
                      <span className="register-modal__validation-dot" />
                      Mindestens 1 Sonderzeichen
                    </div>
                  </div>
                </div>

                <div className="register-modal__actions">
                  <button
                    type="button"
                    className="register-modal__btn register-modal__btn--back"
                    onClick={handleClose}
                  >
                    Zurueck
                  </button>
                  <button
                    type="submit"
                    className="register-modal__btn register-modal__btn--primary"
                    disabled={!isStepOneValid}
                  >
                    Weiter
                  </button>
                </div>

                <p className="register-modal__login-link">
                  Bereits ein Konto?{" "}
                  <button
                    type="button"
                    className="register-modal__link"
                    onClick={handleClose}
                  >
                    Jetzt einloggen
                  </button>
                </p>
              </form>
            </>
          ) : (
            <>
              <h2 className="register-modal__title">
                Erzaehlen Sie uns von Ihrem Unternehmen
              </h2>

              <form
                className="register-modal__form"
                onSubmit={handleStepTwoSubmit}
                noValidate
              >
                <div className="register-modal__company-card">
                  <div className="register-modal__field">
                    <label htmlFor="companyName">
                      Unternehmensname{" "}
                      <span className="register-modal__required">*</span>
                    </label>
                    <input
                      type="text"
                      id="companyName"
                      placeholder="Mesina"
                      value={stepTwo.companyName}
                      onChange={(e) =>
                        handleStepTwoChange("companyName", e.target.value)
                      }
                      required
                    />
                  </div>

                  <div className="register-modal__field">
                    <label htmlFor="companyWebsite">
                      Unternehmenswebsite{" "}
                      <span className="register-modal__required">*</span>
                    </label>
                    <input
                      type="text"
                      id="companyWebsite"
                      placeholder="www.mesina.ai"
                      value={stepTwo.companyWebsite}
                      onChange={(e) =>
                        handleStepTwoChange("companyWebsite", e.target.value)
                      }
                      required
                    />
                  </div>

                  <div className="register-modal__field">
                    <label htmlFor="companySize">
                      Unternehmensgroesse{" "}
                      <span className="register-modal__required">*</span>
                    </label>
                    <select
                      id="companySize"
                      value={stepTwo.companySize}
                      onChange={(e) =>
                        handleStepTwoChange("companySize", e.target.value)
                      }
                      required
                    >
                      <option value="" disabled>
                        Bitte waehlen
                      </option>
                      {COMPANY_SIZES.map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="register-modal__field">
                    <label htmlFor="industry">
                      Branche <span className="register-modal__required">*</span>
                    </label>
                    <select
                      id="industry"
                      value={stepTwo.industry}
                      onChange={(e) =>
                        handleStepTwoChange("industry", e.target.value)
                      }
                      required
                    >
                      <option value="" disabled>
                        Bitte waehlen
                      </option>
                      {INDUSTRIES.map((industry) => (
                        <option key={industry} value={industry}>
                          {industry}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="register-modal__field">
                    <label>
                      Module <span className="register-modal__required">*</span>
                    </label>
                    <div
                      className="register-modal__dropdown"
                      ref={moduleDropdownRef}
                    >
                      <button
                        type="button"
                        className={`register-modal__dropdown-toggle ${isModuleDropdownOpen ? "is-open" : ""}`}
                        onClick={() =>
                          setIsModuleDropdownOpen(!isModuleDropdownOpen)
                        }
                      >
                        <span
                          className={
                            stepTwo.module.length === 0
                              ? "register-modal__dropdown-placeholder"
                              : ""
                          }
                        >
                          {stepTwo.module.length === 0
                            ? "Bitte waehlen"
                            : stepTwo.module
                                .map(
                                  (code) =>
                                    MODULES.find((m) => m.code === code)?.label ?? code,
                                )
                                .join(", ")}
                        </span>
                        <span className="register-modal__dropdown-arrow">
                          ▾
                        </span>
                      </button>
                      {isModuleDropdownOpen && (
                        <div className="register-modal__dropdown-menu">
                          {MODULES.map((mod) => (
                            <label
                              key={mod.code}
                              className="register-modal__dropdown-item"
                            >
                              <input
                                type="checkbox"
                                checked={stepTwo.module.includes(mod.code)}
                                onChange={(e) => {
                                  const isChecked = e.target.checked;
                                  const updatedModules = isChecked
                                    ? [...stepTwo.module, mod.code]
                                    : stepTwo.module.filter((m) => m !== mod.code);
                                  handleStepTwoChange(
                                    "module",
                                    updatedModules,
                                  );
                                }}
                              />
                              <span>{mod.label}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {submitError && (
                  <p className="register-modal__error" role="alert">
                    {submitError}
                  </p>
                )}

                <button
                  type="submit"
                  className="register-modal__btn register-modal__btn--primary register-modal__btn--full"
                  disabled={!isStepTwoValid || registerMutation.isPending}
                >
                  {registerMutation.isPending
                    ? "Wird gesendet..."
                    : "Account erstellen"}
                </button>

                <p className="register-modal__legal">
                  Ihre Daten werden in der{" "}
                  <strong>Europaeischen Union</strong> gehostet.
                  <br />
                  Wenn Sie einen Account erstellen, erklaeren Sie sich mit den{" "}
                  <a href="/agb" className="register-modal__link">
                    Nutzungsbedingungen
                  </a>{" "}
                  einverstanden.
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
